import { cn } from '@rocco/utils/cn';
import { useTracker } from '@archipro-website/tracker';
import { useCountryContext } from '@archipro-website/localisation';
import { ReactComponent as InstagramIcon } from '@rocco/icons/svg/brand/instagram.svg';
import { ReactComponent as FacebookIcon } from '@rocco/icons/svg/brand/facebook.svg';
import { ReactComponent as LinkedinIcon } from '@rocco/icons/svg/brand/linkedin.svg';
import { useUser } from '@modules/user';
import { useRootData } from '~/modules/root';
import { getUrlPrefix } from '~/modules/user/component/share-modal/util/getUrlPrefix';
import { addUtmDataToShareUrl } from '~/modules/user/component/share-modal/util/addUtmDataToShareUrl';
import { copyToClipboard } from '~/modules/social-sharing/utils/sharing';
import type { SocialMediaShareType } from '~/modules/moodboards/config';

interface RevampShareLinksProps {
    /** The article's own path, e.g. `/article/my-article`. */
    shareLink: string;
    /** Article the share is attributed to — the UTM content id. */
    articleID: number;
    articleTitle: string;
    /** Hairline circles on the page, or solid black ones in the head. */
    variant?: 'outline' | 'filled';
    className?: string;
}

const circleBase =
    'flex cursor-pointer items-center justify-center rounded-full border border-solid p-0 transition-opacity hover:opacity-70';

/**
 * `outline` is the closing block's row: a hairline circle on the page.
 * `filled` is the head's: a solid black circle at the tag pills' own height,
 * so the two rows either side of the rule sit on one line.
 */
const CIRCLE: Record<'outline' | 'filled', string> = {
    outline: 'size-[2.125rem] border-[#181818] bg-transparent',
    filled: 'size-[2rem] border-[#181818] bg-[#181818]',
};

const ICON: Record<'outline' | 'filled', string> = {
    outline: 'size-[1rem] fill-[#181818]',
    filled: 'size-[0.9375rem] fill-white',
};

/**
 * The networks' share composers. Built here rather than through react-share,
 * which only survives this app's bundling on the client — the article page
 * renders this row on the server.
 */
const buildShareUrl = {
    facebook: (url: string) =>
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    linkedin: (url: string) =>
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
};

/**
 * Shares the article to Instagram, Facebook and LinkedIn (Figma 2497:77394).
 * Carries the same UTM tagging and `Share` / `ShareSubmit` events as the legacy
 * share modal so the series stays continuous across the two article pages.
 */
export const RevampShareLinks = ({
    shareLink,
    articleID,
    articleTitle,
    variant = 'outline',
    className,
}: RevampShareLinksProps) => {
    const tracker = useTracker();
    const user = useUser();
    const { env } = useRootData();
    const { code } = useCountryContext();

    // The canonical public URL, not the current origin: a share from a branch
    // deploy or a preview host has to land on the live article.
    const shareUrl = shareLink.startsWith('http')
        ? shareLink
        : `${getUrlPrefix(code, env)}${shareLink}`;

    const withUtm = (socialType: SocialMediaShareType) =>
        addUtmDataToShareUrl({
            url: shareUrl,
            socialType,
            pageID: articleID,
            user,
        });

    const trackShare = (socialType: SocialMediaShareType) => {
        tracker.log('Share', {
            url: new URL(shareUrl),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                ExtraData: socialType,
            },
            ga4Data: { socialType },
        });
        tracker.log('ShareSubmit', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });
    };

    // Instagram has no web share intent, so hand the link to the OS share
    // sheet where there is one (mobile, where the app can receive it) and fall
    // back to the clipboard on desktop.
    const shareToInstagram = async () => {
        trackShare('instagram');
        const url = withUtm('instagram');

        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({ title: articleTitle, url });
            } catch {
                // Dismissing the share sheet rejects — nothing to recover from.
            }
            return;
        }

        await copyToClipboard(url);
    };

    return (
        <ul
            className={cn(
                'm-0 flex list-none items-center gap-[0.375rem] p-0',
                className,
            )}
        >
            <li>
                <button
                    type="button"
                    aria-label="Share on Instagram"
                    onClick={() => void shareToInstagram()}
                    className={cn(circleBase, CIRCLE[variant])}
                >
                    <InstagramIcon
                        className={ICON[variant]}
                        aria-hidden="true"
                    />
                </button>
            </li>
            <li>
                <a
                    href={buildShareUrl.facebook(withUtm('facebook'))}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on Facebook"
                    onClick={() => trackShare('facebook')}
                    className={cn(circleBase, CIRCLE[variant])}
                >
                    <FacebookIcon
                        className={ICON[variant]}
                        aria-hidden="true"
                    />
                </a>
            </li>
            <li>
                <a
                    href={buildShareUrl.linkedin(withUtm('linkedin'))}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on LinkedIn"
                    onClick={() => trackShare('linkedin')}
                    className={cn(circleBase, CIRCLE[variant])}
                >
                    <LinkedinIcon
                        className={ICON[variant]}
                        aria-hidden="true"
                    />
                </a>
            </li>
        </ul>
    );
};
